import React, { lazy, Suspense, useMemo } from "react";
import ARProductViewer from "../ar/ARProductViewer";
import AdapterLoadBoundary from "../../core/workspace/3d/AdapterLoadBoundary";

const CurtainComposerPreview = lazy(() => import("../../products/CurtainComposerPreview"));

function modeLabel(mode) {
  return {
    image: "Image",
    video: "Video",
    "3d": "3D",
    document: "Document",
    parametric: "Live",
  }[mode] || "Reality";
}

function availableRealityModes(reality) {
  const modes = [];
  const reps = reality?.representations || {};
  const heroKind = reality?.media?.hero?.kind || null;

  if (reps.video) modes.push("video");
  if (reps.image && reality?.media?.poster?.fallback !== true) modes.push("image");
  if (reps.model3d) modes.push("3d");
  if (reps.document || heroKind === "document") modes.push("document");
  if (heroKind === "parametric") modes.push("parametric");

  return [...new Set(modes)];
}

function preferredRealityMode(reality) {
  const modes = availableRealityModes(reality);
  const heroKind = reality?.media?.hero?.kind;
  const heroMode = heroKind === "model3d" ? "3d" : heroKind;
  // Ready public Film leads the media sequence for the published revision.
  if (modes.includes("video")) return "video";
  if (modes.includes(heroMode) && ["document", "parametric"].includes(heroMode)) return heroMode;
  return modes[0] || "image";
}

function formatClock(ms) {
  const total = Math.max(0, Math.round(Number(ms || 0) / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = String(total % 60).padStart(2, "0");
  return `${minutes}:${seconds}`;
}

export default function RealityPlayerStage({
  reality,
  mode,
  onModeChange,
  arRuntimeActive = false,
  scriptRuntime = null,
  spatialSession = null,
  simulationController = null,
  liveState = null,
  liveRole = "viewer",
  onLiveInteraction = null,
}) {
  const modes = useMemo(() => availableRealityModes(reality), [reality]);
  const modelProduct = useMemo(() => ({
    glbUrl: reality?.representations?.model3d || null,
    usdzUrl: reality?.representations?.iosAR || null,
    posterUrl: reality?.cover?.url || null,
    placementProfile: reality?.physical?.placementProfile || "floor",
    mode: "static",
    hideArButton: true,
  }), [reality]);

  const activeMode = modes.includes(mode) ? mode : preferredRealityMode(reality);
  const title = reality?.identity?.title || "Reality";
  const image = reality?.representations?.image || reality?.cover?.url || null;
  const video = reality?.representations?.video || null;
  const document = reality?.representations?.document || null;
  const scripted = Boolean(scriptRuntime?.ready);
  const stageState = liveState?.stage || scriptRuntime?.stage || {};
  const stageStyle = {
    transform: `translate(${Number(stageState.translateX || 0)}%, ${Number(stageState.translateY || 0)}%) scale(${Number(stageState.scale || 1)}) rotate(${Number(stageState.rotateDeg || 0)}deg)`,
    opacity: Number.isFinite(Number(stageState.opacity)) ? Number(stageState.opacity) : 1,
  };

  const emitStageTap = event => {
    const interactive = event.target?.closest?.("button,a,video,input,select,textarea");
    if (interactive) return;
    if (liveState && liveRole === "participant") {
      onLiveInteraction?.({ type: "event.emit", payload: { event: "tap" } });
      return;
    }
    if (scripted) scriptRuntime.emitEvent?.("tap");
  };

  return (
    <div className="reality-player-stage">
      <div className="reality-player-stage__canvas" data-player-mode={activeMode} onClick={emitStageTap}>
        <div className="reality-player-stage__content" style={stageStyle}>
          {activeMode === "image" && image ? (
            <img src={image} alt={title} />
          ) : null}

          {activeMode === "video" && video ? (
            <video
              src={video}
              poster={reality?.cover?.url || undefined}
              controls
              playsInline
              preload="metadata"
              aria-label={`${title} video`}
            />
          ) : null}

          {activeMode === "3d" && reality?.representations?.model3d && !arRuntimeActive ? (
            <div className="reality-player-stage__model">
              <ARProductViewer product={modelProduct} arDisabled />
            </div>
          ) : null}

          {activeMode === "document" ? (
            <div className="reality-player-document">
              <span className="reality-player-document__glyph" aria-hidden="true">▤</span>
              <strong>{title}</strong>
              <p>This Reality is a document. Open the preserved public representation to inspect it.</p>
              {document ? (
                <a href={document} target="_blank" rel="noreferrer">Open document ↗</a>
              ) : (
                <span>Document state is preserved in this Reality.</span>
              )}
            </div>
          ) : null}

          {activeMode === "parametric" && !arRuntimeActive ? (
            spatialSession ? (
              <div className="reality-player-curtain">
                <AdapterLoadBoundary label="3D preview"><Suspense fallback={<div role="status">Opening 3D preview…</div>}><CurtainComposerPreview
                  session={spatialSession}
                  simulationController={simulationController}
                  simulationProfile={reality?.simulation || spatialSession?.simulation || null}
                  className="reality-player-curtain__canvas"
                /></Suspense></AdapterLoadBoundary>
                {reality?.simulation ? (
                  <div className="reality-player-simulation-label" role="status">
                    <strong>{reality.simulation.label}</strong>
                    <span>{reality.simulation.disclaimer}</span>
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="reality-player-parametric">
                {image ? <img src={image} alt="" aria-hidden="true" /> : null}
                <div>
                  <span className="reality-player-parametric__glyph" aria-hidden="true">◫</span>
                  <strong>Parametric Reality</strong>
                  <p>Preparing the live spatial representation.</p>
                </div>
              </div>
            )
          ) : null}

          {!modes.length ? (
            <div className="reality-player-empty">
              <strong>Reality media unavailable</strong>
              <span>The public identity is preserved, but no playable representation is ready.</span>
            </div>
          ) : null}
        </div>

        {(liveState?.overlay || scriptRuntime?.overlay) ? (() => {
          const overlay = liveState?.overlay || scriptRuntime?.overlay;
          return (
            <div
              className={`reality-script-overlay reality-script-overlay--${overlay.position || "bottom"} reality-script-overlay--${overlay.tone || "neutral"}`}
              role="status"
            >
              {overlay.text}
            </div>
          );
        })() : null}

        {arRuntimeActive ? (
          <div className="reality-player-ar-active" role="status">
            <span>AR session active</span>
          </div>
        ) : null}
      </div>

      {modes.length > 1 ? (
        <div className="reality-player-modes" aria-label="Reality representations">
          {modes.map(item => (
            <button
              key={item}
              type="button"
              className={activeMode === item ? "active" : ""}
              onClick={() => onModeChange?.(item)}
              aria-pressed={activeMode === item}
            >
              {modeLabel(item)}
            </button>
          ))}
        </div>
      ) : null}

      {scripted && !liveState ? (
        <div className="reality-script-controls" aria-label="Reality playback">
          <button
            type="button"
            onClick={scriptRuntime.status === "playing" ? scriptRuntime.pause : scriptRuntime.play}
            aria-label={scriptRuntime.status === "playing" ? "Pause Reality" : "Play Reality"}
          >
            {scriptRuntime.status === "playing" ? "Pause" : scriptRuntime.status === "paused" ? "Resume" : "Play"}
          </button>
          <div className="reality-script-progress" aria-hidden="true">
            <i style={{ width: `${Math.max(0, Math.min(1, scriptRuntime.progress || 0)) * 100}%` }} />
          </div>
          <span>{formatClock(scriptRuntime.timeMs)} / {formatClock(scriptRuntime.durationMs)}</span>
          <button type="button" onClick={scriptRuntime.stop}>Reset</button>
        </div>
      ) : null}
    </div>
  );
}

export { availableRealityModes, preferredRealityMode, modeLabel, formatClock };
